/**
 * reviewQueueModel — pure display derivations for the Needs-review surfaces
 * (BON-381), framework-free so the queue table, the phone cards and the detail
 * page all read the same labels and the labels are unit-testable on their own.
 *
 * Two deliberate departures from the design prototype live here:
 *   - the reason is FREE TEXT (`reviewReason`), not a code with a per-reason
 *     label/blurb/tone — the payment path writes it as prose, sometimes several
 *     joined with "; ", so there is nothing to look up and nothing to rank;
 *   - the flagged column reads an AGE IN DAYS rather than an absolute stamp,
 *     with the instant kept for the row's `title`.
 */
import { plural } from '../../utils/counts';

import type { ReviewHistoryEntry, ReviewKind, ReviewQueueItem } from './reviewTypes';

const EM_DASH = '—';

/** Age of a flag in whole days. `ageDays` is computed server-side from
 *  `flaggedAt`; a zero (or a negative, from clock skew on a row flagged
 *  seconds ago) reads "Today" rather than "0 days". */
export function ageLabel(ageDays: number): string {
  if (!Number.isFinite(ageDays) || ageDays <= 0) return 'Today';
  return `${ageDays} ${ageDays === 1 ? 'day' : 'days'}`;
}

/** Absolute instant in the house format, e.g. `Nov 2, 2026 · 9:16a`. Used for
 *  the `title` behind an age, and for the timeline/history rows. */
export function formatInstant(iso: string | null): string {
  if (!iso) return EM_DASH;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return EM_DASH;
  const date = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const time = d
    .toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    // `\s` also matches the narrow no-break space newer ICU emits.
    .replace(/\s*AM$/, 'a')
    .replace(/\s*PM$/, 'p');
  return `${date} · ${time}`;
}

/** Short instant without the year, for dense timeline rows. */
export function formatShortInstant(iso: string | null): string {
  const full = formatInstant(iso);
  return full === EM_DASH ? full : full.replace(/, \d{4} · /, ' · ');
}

/** The flag reason exactly as the payment path wrote it. */
export function reasonText(reviewReason: string | null): string {
  const trimmed = (reviewReason ?? '').trim();
  return trimmed === '' ? 'No reason recorded' : trimmed;
}

/**
 * The reason split into its separate clauses.
 *
 * `buildReviewFlags` joins several reasons with "; " — a full workshop AND a
 * lapsed hold is an ordinary combination — and the queue clamps the cell to two
 * lines. So a three-reason row showed reason one and gave NO sign the other two
 * existed: the full string was on the `title`, which is mouse-only and invisible
 * on a phone. The fixtures were all single-clause, which is why this looked fine.
 *
 * Splitting lets the cell show the first clause and say how many more there are.
 */
export function reasonClauses(reviewReason: string | null): string[] {
  const trimmed = (reviewReason ?? '').trim();
  if (trimmed === '') return ['No reason recorded'];
  return trimmed
    .split(';')
    .map((part) => part.trim())
    .filter((part) => part !== '');
}

/** "+2 more" for the clauses the clamp cannot show, or null when it shows all. */
export function extraReasonCount(reviewReason: string | null): number {
  return Math.max(0, reasonClauses(reviewReason).length - 1);
}

/**
 * Stripe's own status vocabulary, in the operator's.
 *
 * The API sends `status` verbatim and deliberately, so the audit trail records
 * what Stripe actually said. But `requires_payment_method` is not a sentence, and
 * the reader of this page is an operator deciding what to do — not someone who
 * knows Stripe's state machine. The mock mapped these; the implementation sent
 * the raw enum through.
 *
 * An unrecognised status falls back to the raw string with its underscores
 * opened out, because a NEW Stripe status must never render as blank or as a
 * guess — better an ugly true label than a pretty wrong one.
 */
const STRIPE_STATUS_LABELS: Record<string, string> = {
  succeeded: 'Captured',
  requires_capture: 'Authorized',
  requires_action: 'Needs action',
  requires_confirmation: 'Needs action',
  requires_payment_method: 'Declined',
  processing: 'Processing',
  canceled: 'Cancelled',
};

export function stripeStatusLabel(status: string): string {
  const known = STRIPE_STATUS_LABELS[status.toLowerCase()];
  if (known) return known;
  const opened = status.replace(/_/g, ' ').trim();
  return opened === '' ? 'No payment' : opened.charAt(0).toUpperCase() + opened.slice(1);
}

/**
 * A resolved item is one a HUMAN closed — read off the API's `state`, never
 * re-derived.
 *
 * `!needsReview` is the wrong test and used to be the one here: it is also true
 * of a `superseded` item, which left the queue with nobody resolving it. The two
 * layers disagreed for exactly that row, and the disagreement surfaced as the
 * flag card attributing a resolution to a person who never acted.
 */
export function isResolved(item: Pick<ReviewQueueItem, 'state'>): boolean {
  return item.state === 'resolved';
}

/** Split a section's rows into open + resolved, preserving the API's
 *  oldest-first order within each side (no client-side re-sorting: the order
 *  IS the ageing signal).
 *
 *  A `superseded` row is neither, and the queue endpoints do not return one — but
 *  if one ever arrives it is grouped with `resolved`, i.e. out of the operator's
 *  way, rather than presented as work still to do. */
export function partitionByState<T extends Pick<ReviewQueueItem, 'state'>>(
  rows: T[],
): { open: T[]; resolved: T[] } {
  const open: T[] = [];
  const resolved: T[] = [];
  for (const row of rows) (row.state === 'open' ? open : resolved).push(row);
  return { open, resolved };
}

export interface ResolvedStamp {
  name: string;
  at: string;
}

/**
 * Who resolved it and when — the muted row's right-hand column, and the flag
 * card's stamp. Null while the item is still open, and ALSO null when it left
 * the queue without anyone resolving it.
 *
 * That second case is real: a `superseded` item has `needsReview` cleared with
 * `reviewedAt` / `reviewedBy` deliberately untouched, because no human acted —
 * the flag moved to the booking that now exists. Printing "Resolved by Unknown ·
 * —" over an event nobody performed is the one thing an audit surface may not do,
 * and gating on `state === 'resolved'` is what rules it out by construction
 * rather than by remembering to check.
 *
 * "Unknown" survives for a half-populated resolve (an instant with no actor),
 * where a resolution demonstrably happened and only the name is missing.
 */
export function resolvedStamp(
  item: Pick<ReviewQueueItem, 'state' | 'reviewedAt' | 'reviewedBy'>,
): ResolvedStamp | null {
  if (!isResolved(item)) return null;
  if (!item.reviewedAt && !item.reviewedBy) return null;
  return { name: item.reviewedBy?.name ?? 'Unknown', at: formatInstant(item.reviewedAt) };
}

/**
 * How many resolved rows are actually on the page.
 *
 * The "Show resolved (N)" toggle quoted the API's `resolvedCount`, which is a
 * region-wide total across BOTH kinds and is deliberately independent of the
 * filter — so the number on the switch and the rows it revealed were counting
 * different populations, and differed for real whenever the per-section cap bit.
 * When the rows are loaded, count THEM; the total is only a stand-in for a number
 * we do not have yet.
 */
export function resolvedOnPage(
  sections: {
    bookings: Pick<ReviewQueueItem, 'state'>[];
    checkouts: Pick<ReviewQueueItem, 'state'>[];
  },
  fallback: number,
  loaded: boolean,
): number {
  if (!loaded) return fallback;
  const count = (rows: Pick<ReviewQueueItem, 'state'>[]) => rows.filter(isResolved).length;
  return count(sections.bookings) + count(sections.checkouts);
}

export interface SectionCopy {
  title: string;
  blurb: string;
  emptyTitle: string;
  emptyBody: string;
}

/** Product copy for the two queue sections, verbatim from the design. */
export function sectionCopy(kind: ReviewKind): SectionCopy {
  if (kind === 'booking') {
    return {
      title: 'Bookings',
      blurb: 'A booking record exists. The customer is expecting a seat.',
      emptyTitle: 'Nothing to review',
      emptyBody: 'Every booking reconciled cleanly.',
    };
  }
  return {
    title: 'Checkout sessions',
    blurb:
      'No booking or gift pass order was created. Money may be held or taken with nothing to show for it.',
    emptyTitle: 'Nothing to review',
    emptyBody: 'Every checkout session either booked or released.',
  };
}

/** The queue's one-line explanation of what is on the page. */
export function queueSummary(openCount: number): string {
  const lead =
    openCount === 0
      ? 'Nothing needs an operator'
      : `${openCount} ${openCount === 1 ? 'item needs' : 'items need'} an operator`;
  return `${lead} — bookings that completed under uncertain circumstances, and checkout sessions whose payment stalled, stuck, or failed part-way through.`;
}

/** The API's own cap (400 on a longer note); enforced here so the composer can
 *  refuse before the round trip. */
export const NOTE_MAX_LENGTH = 2000;

/** Validation for the resolve/reopen note. Both transitions require one, so
 *  both call this. Null means the note is submittable. */
export function noteError(note: string): string | null {
  if (note.trim() === '') return 'Leave a note describing what you did.';
  if (note.length > NOTE_MAX_LENGTH) return `Keep the note under ${NOTE_MAX_LENGTH} characters.`;
  return null;
}

/** Integer cents → localized currency, e.g. `$145.80`. */
export function formatMoney(cents: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'USD',
  }).format(cents / 100);
}

/**
 * What we recorded, less the cash Stripe is actually still holding.
 *
 * NET of refunds, and that is the whole point: a booking captured in full and
 * then refunded in full has `capturedCents === totalCents`, so subtracting
 * capture alone reported "Unreconciled $0.00" in the settled green tone — over an
 * order where the customer has their money back and the seat is still booked.
 * ADR-0019 is explicit that a refund reduces the cash figure, so the reconciler
 * has to see it.
 *
 * A null capture (no intent, or Stripe unreachable) leaves the whole order
 * unreconciled, which is the honest answer when we cannot see the money at all.
 */
export function unreconciledCents(
  totalCents: number,
  capturedCents: number | null,
  refundedCents = 0,
): number {
  if (capturedCents === null) return totalCents;
  return totalCents - (capturedCents - refundedCents);
}

/** "1 ticket" / "N tickets". */
export function ticketsLabel(n: number): string {
  return `${n} ${n === 1 ? 'ticket' : 'tickets'}`;
}

/** Customer name, falling back to email, then a placeholder. */
export function customerLabel(customer: ReviewQueueItem['customer']): string {
  return customer.name?.trim() || customer.email?.trim() || 'Unknown';
}

/** "1 pass" / "N passes". */
export function passesLabel(n: number): string {
  return plural(n, 'pass', 'passes');
}

export const GIFT_PASS_PURCHASE_LABEL = 'Gift pass purchase';

type ReviewWorkshop = NonNullable<ReviewQueueItem['workshop']>;

/** A checkout for gift passes rather than a workshop: the API sends it with no workshop. */
export function isGiftPassPurchase(item: Pick<ReviewQueueItem, 'workshop'>): boolean {
  return item.workshop === null;
}

/** Tickets on a workshop order, passes on a gift-pass purchase. */
export function orderCountLabel(item: Pick<ReviewQueueItem, 'workshop' | 'ticketCount'>): string {
  return isGiftPassPurchase(item) ? passesLabel(item.ticketCount) : ticketsLabel(item.ticketCount);
}

/** What the order was for: the workshop's name, or the gift-pass label. */
export function purchaseLabel(workshop: ReviewQueueItem['workshop']): string {
  return workshop ? workshopLabel(workshop) : GIFT_PASS_PURCHASE_LABEL;
}

/** The line under {@link purchaseLabel}: where the workshop is, or how many passes. */
export function purchaseDetailLine(
  item: Pick<ReviewQueueItem, 'workshop' | 'ticketCount'>,
): string {
  return item.workshop ? venueLine(item.workshop) : passesLabel(item.ticketCount);
}

/** "Loyal Legion · Portland, OR", collapsing whichever parts are missing. */
export function venueLine(workshop: ReviewWorkshop): string {
  const place = [workshop.city, workshop.state].filter(Boolean).join(', ');
  const parts = [workshop.venueName, place].filter((p): p is string => Boolean(p && p !== ''));
  return parts.length === 0 ? EM_DASH : parts.join(' · ');
}

/** Workshop name, honest about a row whose type never resolved. */
export function workshopLabel(workshop: ReviewWorkshop): string {
  return workshop.name?.trim() || 'Workshop';
}

/** Venue-local start when we have it (a naive wall clock, parsed as local),
 *  else the UTC instant. */
export function workshopWhen(workshop: ReviewWorkshop): string {
  return formatInstant(workshop.localStartDatetime ?? workshop.startsAt);
}

/** The note from the most recent `resolved` entry — what the flag card shows
 *  alongside the reviewer stamp. Null when nothing has been resolved yet, or
 *  when the resolve carried no note (older rows). */
export function latestResolutionNote(history: ReviewHistoryEntry[]): string | null {
  for (let i = history.length - 1; i >= 0; i -= 1) {
    const entry = history[i];
    if (entry.action === 'resolved') return entry.note?.trim() || null;
  }
  return null;
}

/** Who acted. A null actor is the system, never an invented name — this is an
 *  audit surface and no flagger identity is stored anywhere. */
export function actorLabel(actor: { name: string } | null): string {
  return actor?.name ?? 'System';
}
